import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, AlertTriangle } from 'lucide-react';
import './demo.css';

const TOTAL = 35;

const SC = {
  S1: { s: 0,    e: 5,    fi: 0 },
  S2: { s: 5,    e: 13,   fi: 0.4 },
  S3: { s: 13,   e: 16,   fi: 0 },
  S4: { s: 16,   e: 24,   fi: 0.4 },
  S5: { s: 24,   e: 30,   fi: 0.4 },
  S6: { s: 30,   e: 35,   fi: 0 },
};

function useTimeline(duration, onEnd) {
  const [t, setT] = useState(0);
  const r = useRef({ start: null, raf: null, done: false });
  useEffect(() => {
    const state = r.current;
    state.start = performance.now();
    const tick = () => {
      const el = (performance.now() - state.start) / 1000;
      setT(el);
      if (el >= duration) { if (!state.done) { state.done = true; onEnd?.(); } return; }
      state.raf = requestAnimationFrame(tick);
    };
    state.raf = requestAnimationFrame(tick);
    return () => { if (state.raf) cancelAnimationFrame(state.raf); };
  }, [duration, onEnd]);
  return t;
}

/* ─── Chain Nodes ─── */
function ChainRow({ nodes, status }) {
  const isOk = status === 'ok';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
      {nodes.map((n, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{
            width: 160, padding: '14px 16px', borderRadius: 12,
            border: `1px solid ${isOk || n.ok ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`,
            background: isOk || n.ok ? 'rgba(16,185,129,0.04)' : 'rgba(245,158,11,0.04)',
          }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 2 }}>{n.type}</div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color: '#F8FAFC', marginTop: 4 }}>{n.id}</div>
            <div style={{ fontSize: 9, color: isOk || n.ok ? '#10B981' : '#F59E0B', marginTop: 4 }}>
              {isOk || n.ok ? '◆ AUDIT-GRADE' : n.status || '⚠ CONTROL GAP'}
            </div>
          </div>
          {i < nodes.length - 1 && (
            <div style={{ width: 60, textAlign: 'center' }}>
              <div style={{ height: 2, background: isOk || n.ok ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)' }} />
              <div style={{ fontSize: 8, color: isOk ? '#10B981' : '#475569', marginTop: 2 }}>
                {isOk || n.ok ? '◆ Verified' : ''}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const cleanNodes = [
  { type: 'CER', id: 'CER-2026-001', ok: true },
  { type: 'CSS', id: 'CSS-047832', ok: true },
  { type: 'DER', id: 'DER-047832', ok: true },
  { type: 'OER', id: 'OER-047832', ok: true },
  { type: 'PKG', id: 'PKG-Q1-003', ok: true },
];

const brokenNodes = [
  { type: 'CER', id: 'CER-2026-001', ok: true },
  { type: 'CSS', id: 'CSS-031204', ok: false, status: '⚠ CONTROL GAP' },
  { type: 'DER', id: 'DER-031204', ok: false, status: '⚠ INTEGRITY' },
  { type: 'OER', id: 'OER-031105', ok: false, status: '⚠ WEAK SIGNALS' },
  { type: 'PKG', id: 'PKG-Q1-004', ok: false, status: '⏸ BLOCKED' },
];

export default function TemporalDemo() {
  const nav = useNavigate();
  const onEnd = useCallback(() => nav('/demo'), [nav]);
  const t = useTimeline(TOTAL, onEnd);

  const fade = (at, d = 0.5) => Math.min(1, Math.max(0, (t - at) / d));
  const prog = (a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));
  const lOp = (sc) => {
    if (sc.fi === 0) return t >= sc.s && t < sc.e ? 1 : 0;
    const fs = sc.s - sc.fi;
    if (t < fs) return 0;
    if (t < sc.s) return (t - fs) / sc.fi;
    if (t < sc.e) return 1;
    return 0;
  };

  return (
    <div className="demo-root" data-testid="temporal-demo">
      {/* S1 — Black: April 14 compliant */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.S1), zIndex: 1 }}>
        <div className="demo-center-stack">
          <p className="demo-hero" style={{ opacity: fade(0.5, 0.5) }}>April 14, 2026.</p>
          <p className="demo-hero" style={{ opacity: fade(1.2, 0.5), marginTop: 12, color: '#10B981' }}>
            Governance state: compliant. Proof chain: complete.
          </p>
        </div>
      </div>

      {/* S2 — Clean chain (all green) */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.S2), zIndex: 2 }}>
        <div style={{ transform: `scale(${1 + prog(SC.S2.s, SC.S2.e) * 0.03})` }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 9, color: '#475569', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 16 }}>
              Legal Document Drafting · April 14, 2026
            </div>
            <ChainRow nodes={cleanNodes} status="ok" />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#10B981', opacity: fade(7.0) }}>
              ◆ COMPLETE GOVERNANCE PROOF CHAIN
            </div>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4, opacity: fade(7.5) }}>
              All 5 layers: ◆ AUDIT-GRADE · 5 independent corroborating sources
            </div>
          </div>
        </div>
      </div>

      {/* S3 — Black: One change */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.S3), zIndex: 3 }}>
        <div className="demo-center-stack">
          <p className="demo-hero" style={{ opacity: fade(13.3, 0.4) }}>April 15, 2026.</p>
          <p className="demo-hero" style={{ opacity: fade(14.0, 0.4), marginTop: 12, color: '#F59E0B' }}>
            One change. Ungoverned.
          </p>
        </div>
      </div>

      {/* S4 — Broken chain */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.S4), zIndex: 4 }}>
        <div style={{ transform: `scale(${1 + prog(SC.S4.s, SC.S4.e) * 0.03})` }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 9, color: '#F59E0B', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 16 }}>
              Legal Document Drafting · April 15, 2026 · 11:14 UTC
            </div>
            <ChainRow nodes={brokenNodes} status="broken" />
          </div>
          <div style={{ textAlign: 'center', opacity: fade(18.0) }}>
            <div className="amber-pulse-text" style={{ fontSize: 20, fontWeight: 700, color: '#F59E0B' }}>
              143 decisions affected
            </div>
            <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4, opacity: fade(19.0) }}>
              CONTROL GAP AT DECISION TIME · External Legal Corpus not on allowlist
            </div>
          </div>
        </div>
      </div>

      {/* S5 — Compare side by side */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.S5), zIndex: 5, padding: '0 5%' }}>
        <div style={{ width: '100%', maxWidth: 1200 }}>
          <div style={{ textAlign: 'center', fontSize: 10, color: '#6366F1', fontWeight: 600, letterSpacing: 2, marginBottom: 20, textTransform: 'uppercase' }}>
            ← What changed →
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* April 14 */}
            <div style={{ borderRadius: 12, border: '1px solid rgba(16,185,129,0.2)', padding: 24, background: 'rgba(16,185,129,0.03)', opacity: fade(25.0) }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#10B981', marginBottom: 12 }}>April 14 — BEFORE</div>
              {['CER · AUDIT-GRADE', 'CSS · All controls active', 'DER · AUDIT-GRADE', 'OER · Defensible signals', 'PKG · Complete'].map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 11, color: '#94A3B8' }}>
                  <CheckCircle2 size={12} style={{ color: '#10B981' }} /> {l}
                </div>
              ))}
            </div>
            {/* April 15 */}
            <div style={{ borderRadius: 12, border: '1px solid rgba(245,158,11,0.2)', padding: 24, background: 'rgba(245,158,11,0.03)', opacity: fade(25.5) }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#F59E0B', marginBottom: 12 }}>April 15 — AFTER</div>
              {[
                { t: 'CER · AUDIT-GRADE', ok: true },
                { t: 'CSS · CONTROL GAP · Corpus not on allowlist', ok: false },
                { t: 'DER · Integrity warning', ok: false },
                { t: 'OER · Weak oversight signals', ok: false },
                { t: 'PKG · Blocked', ok: false },
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 11, color: r.ok ? '#94A3B8' : '#F59E0B' }}>
                  {r.ok ? <CheckCircle2 size={12} style={{ color: '#10B981' }} /> : <AlertTriangle size={12} style={{ color: '#F59E0B' }} />} {r.t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* S6 — Close */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.S6), zIndex: 6 }}>
        <div className="demo-center-stack">
          <p className="demo-hero-large" style={{ opacity: fade(30.5, 0.6), maxWidth: 900, fontSize: '1.8rem' }}>
            Apex Trust proves what was true then.
          </p>
          <p className="demo-hero-large" style={{ opacity: fade(31.5, 0.5), marginTop: 16, fontSize: '1.3rem', color: '#6366F1' }}>
            With a signed proof chain.
          </p>
        </div>
      </div>
    </div>
  );
}
